<script lang="ts">
  /**
   * The hotspot ranking (brief §14).
   *
   * It doubles as the map's list alternative for accessibility: a screen reader cannot pan a
   * canvas, and this is the same information in a form it can read. That is why it lists every
   * hotspot in the current view rather than only the top three.
   */
  import { request } from './api.ts';
  import { gdiColour, gdiLabel, vibeSymbol } from './gdi-scale.ts';
  import { describeError } from './session.svelte.ts';
  import type { Hotspot } from './types.ts';

  type Props = {
    bbox: [number, number, number, number] | undefined;
    onclose: () => void;
    onpick: (cell: string) => void;
  };

  const { bbox, onclose, onpick }: Props = $props();

  let sort = $state<'gdi' | 'vibe'>('gdi');
  let hotspots = $state<Hotspot[]>([]);
  let error = $state('');
  let loading = $state(true);

  $effect(() => {
    const query = new URLSearchParams({ sort, limit: '20' });
    if (bbox !== undefined) query.set('bbox', bbox.map((value) => value.toFixed(4)).join(','));
    void (async () => {
      loading = true;
      try {
        hotspots = (await request<{ hotspots: Hotspot[] }>(`/api/hotspots?${query}`)).hotspots;
        error = '';
      } catch (cause) {
        error = describeError(cause);
      } finally {
        loading = false;
      }
    })();
  });
</script>

<section class="panel" aria-label="Hotspots">
  <header>
    <h2>Hotspots</h2>
    <button class="close" onclick={onclose} aria-label="Schließen">×</button>
  </header>

  <div class="tabs" role="tablist">
    <button role="tab" aria-selected={sort === 'gdi'} onclick={() => (sort = 'gdi')}>
      Wo ist was los
    </button>
    <button role="tab" aria-selected={sort === 'vibe'} onclick={() => (sort = 'vibe')}>
      Wo ist es gut
    </button>
  </div>

  {#if error}
    <p class="hint error" role="alert">{error}</p>
  {:else if loading}
    <p class="hint">Einen Moment …</p>
  {:else if hotspots.length === 0}
    <p class="hint">In diesem Ausschnitt ist noch nichts gemeldet.</p>
  {:else}
    <ol>
      {#each hotspots as spot, index (spot.cell)}
        <li>
          <button onclick={() => onpick(spot.cell)}>
            <span class="rank">{index + 1}</span>
            <span class="badge" style={`--gdi-colour: ${gdiColour(spot.gdi)}`}>{spot.gdi}</span>
            <span class="what">
              {gdiLabel(spot.gdi)}
              <small>{spot.sightingCount} Meldungen {vibeSymbol(spot.vibe)}</small>
            </span>
          </button>
        </li>
      {/each}
    </ol>
  {/if}
</section>

<style>
  /* Placed by the bottom-left column in App.svelte. It used to anchor itself 5.5rem above the
     bottom edge, which put it across the legend and the footer chips on a phone. */
  .panel {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    width: min(26rem, 100%);
    max-height: 50vh;
    overflow-y: auto;
    padding: 0.75rem 1rem 1rem;
    border: 1px solid var(--border);
    border-radius: 1rem;
    background: var(--card);
    box-shadow: 0 8px 32px rgb(0 0 0 / 25%);
  }

  header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  h2 {
    margin: 0;
    font-size: 1rem;
  }

  .close {
    border: 0;
    background: none;
    color: var(--muted);
    font-size: 1.5rem;
    line-height: 1;
    cursor: pointer;
  }

  .tabs {
    display: flex;
    gap: 0.4rem;
  }

  .tabs button {
    flex: 1;
    padding: 0.4rem;
    border: 1px solid var(--border);
    border-radius: 0.5rem;
    background: var(--bg);
    color: var(--muted);
    font-size: 0.8125rem;
    cursor: pointer;
  }

  .tabs button[aria-selected='true'] {
    border-color: var(--accent);
    color: var(--fg);
  }

  ol {
    display: grid;
    gap: 0.3rem;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  ol button {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    width: 100%;
    padding: 0.4rem 0.5rem;
    border: 0;
    border-radius: 0.5rem;
    background: none;
    color: var(--fg);
    text-align: left;
    cursor: pointer;
  }

  ol button:hover {
    background: var(--bg);
  }

  .rank {
    width: 1.2rem;
    color: var(--muted);
    font-size: 0.8125rem;
  }

  .badge {
    display: grid;
    place-items: center;
    min-width: 2.2rem;
    padding: 0.2rem 0.4rem;
    border: 1px solid var(--gdi-colour);
    border-radius: 0.4rem;
    background: color-mix(in srgb, var(--gdi-colour) 30%, transparent);
    font-size: 0.875rem;
    font-weight: 600;
  }

  .what {
    display: grid;
    font-size: 0.875rem;
  }

  .what small {
    color: var(--muted);
    font-size: 0.75rem;
  }

  .hint {
    margin: 0;
    font-size: 0.8125rem;
    color: var(--muted);
  }

  .error {
    color: var(--accent);
  }
</style>
